import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ReceiptSharePage = () => {
  const { token } = useParams();

  const [step, setStep] = useState(token ? 'upload' : 'invalid'); // 'upload' | 'scanning' | 'success' | 'invalid'
  const [link, setLink] = useState(null);
  const [invalidMessage, setInvalidMessage] = useState('This upload link is incomplete. Ask your accounting team for your personal link.');
  const [expenseType, setExpenseType] = useState('fleet_lease');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [aiResult, setAiResult] = useState(null);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Validate the link up front so drivers learn about a dead link before taking a photo.
  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/accounting/shareable-links/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (res.ok) setLink(await res.json());
        else if (res.status === 404 || res.status === 410) {
          const data = await res.json().catch(() => ({}));
          setInvalidMessage(data.detail || 'This upload link is no longer active. Ask your accounting team for a new one.');
          setStep('invalid');
        }
      })
      .catch(() => {}); // offline/unreachable: the submit will report the error
  }, [token]);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    setFile(selected);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selected);
  };

  const handleClearFile = () => {
    setFile(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!preview && !file) {
      setErrorMessage('Please upload or capture a receipt image first.');
      return;
    }

    setErrorMessage('');
    setStep('scanning');

    try {
      const payload = {
        token,
        image_data: preview,
        notes: `Expense Type: ${expenseType}`,
        expense_type: expenseType,
        payment_method: 'card'
      };

      const res = await fetch(`${BACKEND_URL}/api/accounting/public-upload-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setAiResult(data.parsed_data);
        setTimeout(() => setStep('success'), 1200);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (res.status === 404 || res.status === 410) {
        setInvalidMessage(data.detail || 'This upload link is no longer active. Ask your accounting team for a new one.');
        setStep('invalid');
        return;
      }
      setErrorMessage(
        res.status === 409
          ? 'This receipt was already uploaded — no need to send it again.'
          : data.detail || 'The receipt could not be uploaded. Please try again.'
      );
      setStep('upload');
    } catch (err) {
      console.error('Upload error:', err);
      setErrorMessage('No connection — the receipt was NOT uploaded. Please try again when you have signal.');
      setStep('upload');
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setExpenseType('fleet_lease');
    setAiResult(null);
    setStep('upload');
  };

  if (step === 'invalid') {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-white font-sans">
        <div className="max-w-sm w-full bg-slate-900 border border-red-500/40 rounded-2xl p-6 text-center space-y-3">
          <div className="text-3xl">🔒</div>
          <h2 className="text-lg font-bold">Link not available</h2>
          <p className="text-sm text-slate-300">{invalidMessage}</p>
        </div>
      </div>
    );
  }

  // Scanning animation view
  if (step === 'scanning') {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-white font-sans">
        <div className="relative w-80 h-96 bg-slate-900 border border-blue-500/40 rounded-3xl overflow-hidden shadow-2xl flex flex-col items-center justify-center p-6 text-center">
          {preview && (
            <img src={preview} alt="Receipt preview" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-xs" />
          )}
          <div className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent shadow-[0_0_15px_#3b82f6] animate-bounce top-1/3" />
          <div className="relative z-10 space-y-3 bg-slate-950/80 backdrop-blur-md p-6 rounded-2xl border border-slate-800">
            <div className="w-14 h-14 bg-blue-600/30 text-blue-400 rounded-full flex items-center justify-center mx-auto text-2xl animate-pulse">
              🤖
            </div>
            <h3 className="text-lg font-bold text-white">AI Processing Receipt</h3>
            <p className="text-xs text-blue-300">Extracting transaction details & routing to TMS Accounting...</p>
          </div>
        </div>
      </div>
    );
  }

  // Success Confirmation Screen
  if (step === 'success' && aiResult) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
        <header className="bg-slate-900/90 backdrop-blur border-b border-slate-800 p-4 sticky top-0 z-20">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-500/20 text-emerald-400 rounded-xl flex items-center justify-center font-bold text-lg">
                ✓
              </div>
              <div>
                <h1 className="font-bold text-white text-base">Receipt Submitted</h1>
                <p className="text-xs text-slate-400">TMS Accounting Portal</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 max-w-md w-full mx-auto p-4 space-y-4">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 text-center space-y-2">
            <div className="w-14 h-14 rounded-full bg-emerald-500 text-slate-950 font-bold text-2xl mx-auto flex items-center justify-center mb-2">
              ✓
            </div>
            <h2 className="text-xl font-bold text-white">Upload Complete</h2>
            <p className="text-xs text-emerald-300">
              Your expense receipt has been submitted directly into the TMS Accounting Vault.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-3 shadow-xl">
            <div className="flex justify-between items-center text-xs text-slate-400 border-b border-slate-800 pb-2">
              <span>Receipt ID:</span>
              <span className="font-mono text-white font-semibold">{aiResult.receipt_number}</span>
            </div>
            <div className="flex justify-between items-center text-xs text-slate-400 border-b border-slate-800 pb-2">
              <span>Vendor:</span>
              <span className="text-white font-semibold">{aiResult.vendor_name}</span>
            </div>
            <div className="flex justify-between items-center text-xs text-slate-400 border-b border-slate-800 pb-2">
              <span>Amount:</span>
              <span className="text-emerald-400 font-bold text-base">${Number(aiResult.amount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-xs text-slate-400">
              <span>Category:</span>
              <span className="text-blue-300 font-medium capitalize">{aiResult.category?.replace('_', ' ')}</span>
            </div>
          </div>

          <button
            onClick={handleReset}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center space-x-2"
          >
            <span>➕ Upload Another Receipt</span>
          </button>
        </main>
      </div>
    );
  }

  // Ultra-short 3-field form
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      <header className="bg-slate-900/90 backdrop-blur border-b border-slate-800 p-4 sticky top-0 z-20">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600/20 border border-blue-500/30 text-blue-400 rounded-xl flex items-center justify-center text-xl">
              🧾
            </div>
            <div>
              <h1 className="font-bold text-white text-base">Expense Upload Form</h1>
              <p className="text-xs text-blue-400">
                {link?.driver_name || link?.submitter_name ? `Receipts for ${link.driver_name || link.submitter_name}` : 'No Login Required'}
                {link?.company_name ? ` • ${link.company_name}` : ''}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-md w-full mx-auto p-4 space-y-5">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-2xl space-y-5">
          {errorMessage && (
            <div className="bg-red-950/60 border border-red-500/50 p-3 rounded-xl text-red-300 text-xs flex items-center gap-2">
              <span>⚠️</span>
              <span>{errorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 1. Expense Type */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                1. Expense Type *
              </label>
              <select
                value={expenseType}
                onChange={(e) => setExpenseType(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 text-white rounded-xl px-3.5 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
              >
                <option value="fleet_lease">🚛 Fleet Rental & Lease</option>
                <option value="insurance">🛡️ Fleet & Cargo Insurance</option>
                <option value="fuel">🛢️ Fuel & Energy</option>
                <option value="repairs_maintenance">🔧 Repairs & Maintenance</option>
                <option value="tolls_permits">🛣️ Tolls, Permits & Licenses</option>
                <option value="facility_rent">🏢 Facility & Warehouse Rent</option>
                <option value="telematics_software">💻 Telematics & Software</option>
                <option value="other_ops">📋 Other Operational Expense</option>
              </select>
            </div>

            {/* 2. Upload Receipt Button */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                2. Receipt Image / Document *
              </label>

              {!preview ? (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-xl border border-blue-400/30 flex flex-col items-center justify-center space-y-1.5 shadow-lg shadow-blue-600/20 active:scale-95 transition-all cursor-pointer"
                  >
                    <span className="text-2xl">📸</span>
                    <span className="font-bold text-xs">Take Photo</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 p-4 rounded-xl border border-slate-700 flex flex-col items-center justify-center space-y-1.5 active:scale-95 transition-all cursor-pointer"
                  >
                    <span className="text-2xl">📁</span>
                    <span className="font-bold text-xs">Upload File</span>
                  </button>

                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="relative rounded-xl overflow-hidden border border-slate-700 bg-slate-950 p-2">
                  <img src={preview} alt="Receipt preview" className="w-full max-h-64 object-contain mx-auto rounded-lg" />
                  <button
                    type="button"
                    onClick={handleClearFile}
                    className="absolute top-3 right-3 bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:bg-red-500 transition-colors text-sm font-bold"
                  >
                    ✕
                  </button>
                  <div className="mt-2 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                    <span className="text-emerald-400">✓ Image Attached</span>
                    <button type="button" onClick={handleClearFile} className="text-blue-400 hover:underline ml-2">
                      Replace
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 3. Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={!preview}
                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-600 text-slate-950 font-black py-4 rounded-xl shadow-xl shadow-emerald-600/20 active:scale-[0.99] transition-all text-base flex items-center justify-center space-x-2 cursor-pointer"
              >
                <span>🚀 Submit Expense</span>
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ReceiptSharePage;
